﻿import { bbox, distance, featureCollection, greatCircle, point } from '@turf/turf';
import type { PaddingOptions } from 'maplibre-gl';
import { FeMap } from '../components/map/fe-map';
import { WorkspaceStateItemFlight } from '../state/slices/workspace-slice';

// ⚠️ A lot of vibecoded code in here

const ENCOMPASS_PADDING = { top: 10, bottom: 20, left: 20, right: 10 };

function normalizeLng(lng: number): number {
    return ((((lng + 180) % 360) + 360) % 360) - 180;
}

function greatCircleSamples(p1: [number, number], p2: [number, number]): [number, number][] {
    if (distance(p1, p2, { units: 'kilometers' }) < 1111) {
        return [];
    }
    const line = greatCircle(p1, p2, { npoints: 16 });
    const coords = line.geometry?.coordinates;
    if (!coords || coords.length <= 2) {
        return [];
    }
    return coords.slice(1, -1) as [number, number][];
}

function unwrapRoute(ordered: [number, number][]): [number, number][] {
    if (ordered.length === 0) {
        return [];
    }
    const result: [number, number][] = [[normalizeLng(ordered[0][0]), ordered[0][1]]];
    for (let i = 1; i < ordered.length; i++) {
        let lng = normalizeLng(ordered[i][0]);
        const prev = result[i - 1][0];
        while (lng - prev > 180) {
            lng -= 360;
        }
        while (lng - prev < -180) {
            lng += 360;
        }
        result.push([lng, ordered[i][1]]);
    }
    return result;
}

function snapToRoute(lng: number, routeCenter: number): number {
    lng = normalizeLng(lng);
    while (lng - routeCenter > 180) {
        lng -= 360;
    }
    while (lng - routeCenter < -180) {
        lng += 360;
    }
    return lng;
}

function buildOrderedRoute(waypoints: [number, number][]): [number, number][] {
    return waypoints.flatMap((p, i, arr) => (i < arr.length - 1 ? [p, ...greatCircleSamples(p, arr[i + 1])] : [p]));
}

function computeBbox(orderedRoute: [number, number][], standalonePoints: [number, number][]): [number, number, number, number] | null {
    const allPts: [number, number][] = [];

    if (orderedRoute.length > 0) {
        const unwrapped = unwrapRoute(orderedRoute);
        allPts.push(...unwrapped);
        const lngs = unwrapped.map(([lng]) => lng);
        const routeCenter = (Math.max(...lngs) + Math.min(...lngs)) / 2;
        for (const [lng, lat] of standalonePoints) {
            allPts.push([snapToRoute(lng, routeCenter), lat]);
        }
    } else {
        allPts.push(...standalonePoints.map(([lng, lat]): [number, number] => [normalizeLng(lng), lat]));
    }

    if (allPts.length === 0) {
        return null;
    }

    return bbox(featureCollection(allPts.map(([lng, lat]) => point([lng, lat])))) as [number, number, number, number];
}

function normalizeBboxWest(b: [number, number, number, number]): [number, number, number, number] {
    const span = b[2] - b[0];
    const west = normalizeLng(b[0]);
    return [west, b[1], west + span, b[3]];
}

export function encompassFlight(map: FeMap, flights: WorkspaceStateItemFlight[], padding?: PaddingOptions) {
    let combined: [number, number, number, number] | null = null;

    for (const f of flights) {
        const pos = f.details.position.isValid ? ([f.details.position.lng, f.details.position.lat] as [number, number]) : null;
        const dest = f.details.destinationAirportPosition.isValid
            ? ([f.details.destinationAirportPosition.lng, f.details.destinationAirportPosition.lat] as [number, number])
            : null;
        const origin = f.details.originAirportPosition.isValid
            ? ([f.details.originAirportPosition.lng, f.details.originAirportPosition.lat] as [number, number])
            : null;
        const flown = f.routes.flown?.map((x) => x.position) ?? [];
        const planned = f.routes.planned?.waypoints.map((x) => x.position) ?? [];
        const atc = f.routes.atc?.waypoints.map((x) => x.position) ?? [];

        const atcRoute = buildOrderedRoute(atc);
        const plannedRoute = buildOrderedRoute(planned);
        // Priority: atc > planned > flown > airport GC (shortest path, last resort)
        const orderedRoute =
            atcRoute.length > 0
                ? atcRoute
                : plannedRoute.length > 0
                  ? plannedRoute
                  : flown.length > 0
                    ? flown
                    : origin && dest
                      ? [origin, ...greatCircleSamples(origin, dest), dest]
                      : [];

        const standalonePoints = ([pos, dest, origin, ...flown] as ([number, number] | null)[]).filter(
            (x): x is [number, number] => x != null
        );

        const rawBbox = computeBbox(orderedRoute, standalonePoints);
        if (!rawBbox) {
            continue;
        }
        const flightBbox = normalizeBboxWest(rawBbox);

        combined = combined
            ? [
                  Math.min(combined[0], flightBbox[0]),
                  Math.min(combined[1], flightBbox[1]),
                  Math.max(combined[2], flightBbox[2]),
                  Math.max(combined[3], flightBbox[3])
              ]
            : flightBbox;
    }

    if (!combined) {
        return;
    }
    
    const combinedPadding: PaddingOptions = {
        top: (padding?.top ?? 0) + ENCOMPASS_PADDING.top,
        bottom: (padding?.bottom ?? 0) + ENCOMPASS_PADDING.bottom,
        left: (padding?.left ?? 0) + ENCOMPASS_PADDING.left,
        right: (padding?.right ?? 0) + ENCOMPASS_PADDING.right,
    };

    map.encompass(combined, combinedPadding);
}
